import { Injectable } from '@angular/core';
import { ITransactions } from '@app/core/models/app.model';
import { PreviewOffer } from '@app/core/models/offers.model';
import { CreditCardInfo } from '@app/core/models/openpay.model';
import { ICreditCard, IPaymentParams } from '@app/core/models/payments.model';
import { Summary } from '@app/core/models/summary.model';
import { OpenpayService } from '@app/domain/services/openpay.service';
import { PaymentService } from '@app/domain/services/payments/payment.service';
import * as paymentsActions from '@app/domain/store/payments/payments.actions';
import * as paymentsSelectors from '@app/domain/store/payments/payments.selectors';
import { Store } from '@ngrx/store';
import * as appSelectors from '@store/app/app.selectors';
import * as offersSelectors from '@store/offers/offers.selectors';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IPaymentResponse } from '../core/models/payments.model';

@Injectable({ providedIn: 'root' })
export class PaymentsFacade {
  public currentRoute$: Observable<string>;
  public isLoadingPayments$: Observable<boolean>;
  public summary$: Observable<Summary>;
  public creditCard$: Observable<ICreditCard>;

  constructor(
    private _store: Store,
    private _paymentService: PaymentService,
    private _openpayService: OpenpayService
  ) {
    this.isLoadingPayments$ = this._store.select(paymentsSelectors.selectIsLoadingPayments);
    this.summary$ = combineLatest([
      this._store.select(appSelectors.selectTransactions),
      this._store.select(offersSelectors.selectDefaultPreviewOffer),
    ]).pipe(
      map(([transaction, previewOffer]: [ITransactions, PreviewOffer]) =>
        this._paymentService.getSummaryByPreviewOfferAndAmount(transaction.amount, previewOffer)
      )
    );
    this.creditCard$ = this._store.select(paymentsSelectors.selectCreditCard);
  }

  public createPayments(paymentParams: IPaymentParams): void {
    this._store.dispatch(paymentsActions.createPayments());
  }

  public createCardToken(creditCardInfo: CreditCardInfo, paymentParams: IPaymentParams): void {
    this._store.dispatch(paymentsActions.getCreditCardType({ creditCardInfo, paymentParams }));
  }

  public validateCardNumber(cardNumber: number): boolean {
    return this._openpayService.validateCardNumber(cardNumber);
  }

  public validateCVC(cardNumber: number): boolean {
    return this._openpayService.validateCVC(cardNumber);
  }

  public validateCVCAndCardNumber(cvc: number, cardNumber: number): boolean {
    return this._openpayService.validateCVCAndCardNumber(cvc, cardNumber);
  }

  public validateExpiry(month: number, year: number): boolean {
    return this._openpayService.validateExpiry(month, year);
  }

  public getDeviceData(): string {
    return this._openpayService.getDeviceData();
  }

  public setDeviceSessionId(deviceSessionId: string) {
    return this._store.dispatch(paymentsActions.setDeviceSessionId({ deviceSessionId }));
  }

  public createPaymentsIfHasCard(paymentParams: IPaymentParams) {
    return this._store.dispatch(paymentsActions.createPaymentIfHasCard({ paymentParams }));
  }

  public setPaymentsParams(paymentParams: IPaymentParams) {
    return this._store.dispatch(paymentsActions.setPaymentsParams({ paymentParams }));
  }

  public redirectToMerchant() {
    return this._store.dispatch(paymentsActions.redirectToMerchant());
  }

  public redirectToMerchantWithLoop() {
    return this._store.dispatch(paymentsActions.redirectToMerchantWithLoop());
  }

  public redirecToPersonalInfo() {
    return this._store.dispatch(paymentsActions.redirecToPersonalInfo());
  }

  public setIsLoadingPayment(isLoading: boolean, step: number) {
    return this._store.dispatch(paymentsActions.setIsLoadingPayment({ isLoading, step }));
  }

  public redirectToPaymentStatus() {
    return this._store.dispatch(paymentsActions.redirectToPaymentStatus());
  }

  public setPaymentErrorCode() {
    return this._store.dispatch(paymentsActions.setPaymentErrorCode());
  }

  public successCreatePayments(paymentResponse: IPaymentResponse) {
    return this._store.dispatch(paymentsActions.successCreatePayments({ paymentResponse }));
  }

  public setLoading(isLoadingPayments: boolean) {
    return this._store.dispatch(paymentsActions.setLoading({ isLoadingPayments }));
  }
}
